import DialogClearStyles, {
  DialogClearStylesButtonsFooter,
  DialogContent,
  DialogFooter,
} from 'components/accounts/DialogClearStyles';
import { PrimaryButton, SecondaryButton } from 'components/v2/buttons';
import { DeleteIcon, InfoIcon } from 'components/v2/icons';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { HeaderDialog } from 'styles/Styles.styled';
import { RemovalDialogContent } from './removalContent';

interface Props {
  /** What to show; the dialog is open while this is not null. */
  content: RemovalDialogContent | null;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}

/**
 * Confirmation for removing a role or an organisation on User Details. It renders the content it is given and knows
 * nothing about roles or APIs. Esc and a click on the backdrop do nothing; only the buttons close it.
 */
const RemoveMembershipDialog: React.FC<Props> = ({ content, onConfirm, onClose }) => {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);
  // Keeps the last content on screen while the dialog fades out after content became null.
  const [lastContent, setLastContent] = useState<RemovalDialogContent | null>(content);
  if (content && content !== lastContent) {
    setLastContent(content);
  }
  const shown = content ?? lastContent;

  const handleDelete = async () => {
    if (isDeleting) {
      return;
    }
    // Disabled from the first click until the removal has finished, so it runs once (AC16).
    setIsDeleting(true);
    try {
      await onConfirm();
    } finally {
      setIsDeleting(false);
    }
  };

  // Strings may mark text as bold with <b>, e.g. "the organisation's <b>account manager</b>".
  const text = (key: string) => <Trans i18nKey={key} t={t} components={{ b: <b /> }} />;

  const hasConsequences = !!shown && shown.consequenceKeys.length > 0;
  // The blocked (account manager) dialog keeps its reason and instruction on separate lines.
  const runsOn = !!shown && !shown.blocked && !hasConsequences;

  return (
    <DialogClearStyles
      visible={content !== null}
      onHide={onClose}
      header={
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', marginTop: 40 }}>
          {shown?.blocked ? <InfoIcon /> : <DeleteIcon />}
          <HeaderDialog style={{ maxWidth: 405, marginBottom: 14, marginLeft: 'auto', marginRight: 'auto', fontSize: 18 }}>
            {shown && t(shown.titleKey)}
          </HeaderDialog>
        </div>
      }
      closable={false}
      draggable={false}
    >
      {shown && (
        <DialogContent style={{ maxWidth: 405, paddingLeft: 0, paddingRight: 0, marginLeft: 60, marginRight: 60 }}>
          <Body $alignLeft={hasConsequences}>
            {runsOn ? (
              // A confirmation without a list reads as one paragraph that ends with the closing line.
              <div>
                {[...shown.paragraphKeys, ...(shown.closingKey ? [shown.closingKey] : [])].map((key, index) => (
                  <React.Fragment key={key}>
                    {index > 0 && ' '}
                    {text(key)}
                  </React.Fragment>
                ))}
              </div>
            ) : (
              <>
                {shown.paragraphKeys.map((key) => (
                  <div key={key}>{text(key)}</div>
                ))}
                {hasConsequences && (
                  <>
                    <div>{t('account.removeMembership.afterRemoval')}</div>
                    <ConsequenceList>
                      {shown.consequenceKeys.map((key) => (
                        <li key={key}>{text(key)}</li>
                      ))}
                    </ConsequenceList>
                  </>
                )}
                {shown.closingKey && <Closing $spaced={hasConsequences}>{text(shown.closingKey)}</Closing>}
              </>
            )}
          </Body>
          {shown.showLastRoleWarning && <LastRoleWarning>{t('account.removeMembership.lastRoleWarning')}</LastRoleWarning>}
          <DialogFooter>
            <DialogClearStylesButtonsFooter>
              {shown.blocked ? (
                <SecondaryButton type="button" label={t('account.removeMembership.close')} onClick={onClose} />
              ) : (
                <>
                  <SecondaryButton
                    type="button"
                    label={t('account.removeMembership.cancel')}
                    onClick={onClose}
                    disabled={isDeleting}
                  />
                  <PrimaryButton
                    type="button"
                    label={t('account.removeMembership.delete')}
                    onClick={handleDelete}
                    disabled={isDeleting}
                    loading={isDeleting}
                  />
                </>
              )}
            </DialogClearStylesButtonsFooter>
          </DialogFooter>
        </DialogContent>
      )}
    </DialogClearStyles>
  );
};

export default RemoveMembershipDialog;

const Body = styled.div<{ $alignLeft: boolean }>`
  font-size: 14px;
  line-height: 20px;
  color: #151515b2;
  text-align: ${(props) => (props.$alignLeft ? 'left' : 'center')};
`;

const ConsequenceList = styled.ul`
  margin: 0;
  padding-left: 20px;
`;

const Closing = styled.div<{ $spaced: boolean }>`
  margin-top: ${(props) => (props.$spaced ? '20px' : '0')};
`;

const LastRoleWarning = styled.div`
  margin-top: 16px;
  padding: 16px;
  font-size: 14px;
  line-height: 20px;
  text-align: left;
  color: ${(props) => props.theme.colors.statusLabelRedText};
  background-color: ${(props) => props.theme.colors.statusLabelRedBg};
`;
